import * as React from 'react';
import { FunctionComponent, useEffect, useState } from 'react';
import { UwGLLocationReview } from 'types/underwriting.type';
import { useSelector } from 'react-redux';
import { getSovLocations, getSubmissionDetails } from 'application/redux/selectors';
import { getLocationOptionsData } from 'application/redux/selectors/locationOptions';
import { ItemTableData } from 'components/ItemTable/ItemTableData';
import { calculateAnnualizedPremiumFromTotalPremium, calculateEndorsementPremium } from 'helpers/calculations';
import { calculateTotalGLPremium } from 'helpers/glCalculationHelper';
import LrDialog from 'components/LrDialog';
import ItemTable from 'components/ItemTable';
import GLPropertyTableDataBuilder from 'components/Underwriting/components/GLPropertyTable/GLPropertyTableDataBuilder';
import GLPropertyTableRowModel from 'components/Underwriting/components/GLPropertyTable/GLPropertyTableRowModel';
import { GLRatingRequest } from 'types/generalLiability.type';
import ShowHTMLTableDialog from 'components/Underwriting/components/ShowHTMLTableDialog';
import { isLocationAddEndorsement } from '../../../../../../helpers/endorsements';

interface Props {
    data: UwGLLocationReview[];
    isOpen: boolean;
    isModiedAdjustmentEndorsement: boolean;
    origEffectiveDate: string;
    origExpDate: string;
    newEffectiveDate: string;
    newExpDate: string;
    onClose: () => void;
    onSave: (locationData: UwGLLocationReview[]) => void;
}

const UWDetailsAdjustmentGLDialog: FunctionComponent<Props> = (props) => {
    const {
        data,
        isOpen,
        isModiedAdjustmentEndorsement,
        onClose,
        onSave,
        origEffectiveDate,
        origExpDate,
        newEffectiveDate,
        newExpDate,
    } = props;

    const locations = useSelector(getSovLocations);
    const locationOptions = useSelector(getLocationOptionsData);
    const submission = useSelector(getSubmissionDetails);

    const [locationItemData, setLocationItemData] = useState<ItemTableData>({} as ItemTableData);
    const [locationData, setLocationData] = useState<UwGLLocationReview[]>(data);
    const [showHTMLTableDialog, setShowHTMLTableDialog] = useState<GLRatingRequest>();
    const [selectedRowData, setSelectedRowData] = useState<GLPropertyTableRowModel>();

    const unformat = (formattedValue: string, asType: string) => {
        if (formattedValue === '-') return formattedValue;
        return asType === 'float'
            ? parseFloat(formattedValue.replace(/[^0-9-.]/g, ''))
            : parseInt(formattedValue.replace(/[^0-9-.]/g, ''), 10);
    };

    const buildRaasPayload = (dataRow: GLPropertyTableRowModel): GLRatingRequest => ({
        genAggLimit: dataRow.genAggLimit,
        premOpsOccurrenceLimit: dataRow.premOpsOccurrenceLimit,
        ratingRequestLocation: {
            experienceCRDB: dataRow.experienceCRDB,
            locationId: dataRow.rowId,
            premOpsDeductible: dataRow.premOpsDeductible,
        },
    });

    const handleShowHTMLTableClick = (rowData: GLPropertyTableRowModel) => {
        setSelectedRowData(rowData);
        setShowHTMLTableDialog(buildRaasPayload(rowData));
    };

    function updateCalculationsOnRow(foundRow: UwGLLocationReview) {
        foundRow.totalPremium = calculateTotalGLPremium(
            foundRow.irpm,
            foundRow.baseRatedPremium,
            foundRow.triaPercentage
        );
        foundRow.annualizedPremium = calculateAnnualizedPremiumFromTotalPremium(
            foundRow.totalPremium,
            origEffectiveDate,
            origExpDate || ''
        );
        foundRow.blendedDoorRate = foundRow.numberUnits ? foundRow.annualizedPremium / foundRow.numberUnits : undefined;

        if (isModiedAdjustmentEndorsement) {
            foundRow.endorsementPremium =
                calculateEndorsementPremium(
                    foundRow.totalPremium,
                    origEffectiveDate,
                    origExpDate || '',
                    newEffectiveDate,
                    newExpDate
                ) || 0;
        }
    }

    useEffect(() => {
        locationData.forEach((ld) => updateCalculationsOnRow(ld));
    }, []);

    const changeMade = (value: string, id: string) => {
        const rowId = id.split(':')[0];
        const cellName = id.split(':')[1];
        const existingRowDataIdx = locationData.findIndex((entry: any) => entry.rowId.toString() === rowId);
        const foundRow = { ...locationData[existingRowDataIdx] };
        const floatFields = ['baseRate', 'irpm', 'scoreIrpm', 'triaPercentage'];
        if (!!cellName && !!value) {
            foundRow[cellName] = floatFields.indexOf(cellName) !== -1 ? unformat(value, 'float') : value;
            if (cellName === 'ratingScore') {
                foundRow.scoreIrpm = Number(locationOptions.LOCATION_GL_SCORE_OPTIONS[value]);
            }
        }

        if (foundRow.irpm && foundRow.scoreIrpm && foundRow.irpm < foundRow.scoreIrpm) {
            foundRow.irpm = foundRow.scoreIrpm;
        }
        updateCalculationsOnRow(foundRow);
        locationData[existingRowDataIdx] = foundRow;
        setLocationData([...locationData]);
    };

    useEffect(() => {
        if (locationData) {
            setLocationItemData(
                GLPropertyTableDataBuilder.build(
                    {},
                    locations,
                    locationOptions,
                    changeMade,
                    handleShowHTMLTableClick,
                    locationData,
                    false,
                    isModiedAdjustmentEndorsement,
                    false,
                    origEffectiveDate,
                    origExpDate
                )
            );
        }
    }, [locationData]);

    useEffect(() => {
        setLocationData(data);
    }, [data]);

    return (
        <LrDialog
            title={'UW Review Details'}
            isOpen={isOpen}
            onSubmit={() => onSave(locationData)}
            onCancel={onClose}
            submitButtonText={'Save'}
            cancelButtonText={'Cancel'}
            width={'1280px'}
        >
            <div style={{ width: '100%', overflowX: 'scroll' }}>
                <ItemTable
                    data={locationItemData}
                    defaultHeaderCellStyle={{ textAlign: 'right' }}
                    defaultBodyCellStyle={{ textAlign: 'right' }}
                />
                {showHTMLTableDialog && (
                    <ShowHTMLTableDialog
                        raasPayload={showHTMLTableDialog}
                        rowData={selectedRowData}
                        closeDialogCallback={() => setShowHTMLTableDialog(undefined)}
                    />
                )}
            </div>
        </LrDialog>
    );
};

export default UWDetailsAdjustmentGLDialog;
